import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { FullAppLoader } from 'soolui';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ScraperProvider } from './contexts/ScraperContext';
import ErrorBoundary from './components/ErrorBoundary';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import PasswordResetPage from './pages/PasswordResetPage';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #f8fafc;
  }
  
  #root {
    min-height: 100vh;
  }
`;

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isSSO } = useAuth();
  
  if (loading) {
    return <FullAppLoader hasSidebar={false} hasTopbar={!isSSO} />;
  }
  
  return user ? <>{children}</> : <Navigate to="/auth" replace />;
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isSSO } = useAuth();
  
  if (loading) {
    return <FullAppLoader hasSidebar={false} hasTopbar={!isSSO} />;
  }
  
  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

const RootRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/dashboard${location.search}${location.hash}`} replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route 
        path="/auth" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/onboarding" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/dashboard/*" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <PasswordResetPage />
          </PublicRoute>
        }
      />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <>
      <GlobalStyle />
      <ErrorBoundary>
        <Router>
          <AuthProvider>
            <ScraperProvider>
              <AppRoutes />
            </ScraperProvider>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </>
  );
};

export default App;
